import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ContactMessage {
  name: string;
  email: string;
  subject: string;
  message: string;
  phone?: string;
}

export interface ContactResponse {
  status: string;
  message: string;
}

export interface ContactInfo {
  status: string;
  data: {
    supportHours: string;
    responseTime: string;
    methods: Array<{
      type: string;
      title: string;
      description: string;
      available: boolean;
    }>;
  };
}

export interface UserTicket {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  category?: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assigned_to?: string;
  resolved_by?: string;
  resolution_notes?: string;
  internal_notes?: string;
  response_count: number;
  responded_at?: string;
  resolved_at?: string;
  last_admin_response?: string;
  last_user_response?: string;
  ip_address?: string;
  user_agent?: string;
  request_id?: string;
  user_id?: string;
  created_at: string;
  updated_at: string;
}

export interface TicketResponse {
  id: string;
  response_text: string;
  response_type: 'email' | 'phone' | 'internal_note';
  is_sent_to_customer: boolean;
  email_subject?: string;
  created_at: string;
  admin_first_name?: string;
  admin_last_name?: string;
  is_admin_response: boolean;
}

export interface TicketDetails {
  ticket: UserTicket;
  responses: TicketResponse[];
}

export interface UserTicketsResponse {
  status: string;
  message: string;
  data: UserTicket[];
}

export interface TicketDetailsResponse {
  status: string;
  message: string;
  data: TicketDetails;
}

export interface TicketReplyRequest {
  message: string;
}

export interface TicketReplyResponse {
  status: string;
  message: string;
  data: TicketResponse;
}

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private apiUrl = `${environment.apiUrl}/contact`;

  constructor(private http: HttpClient) {}

  sendContactMessage(contactData: ContactMessage): Observable<ContactResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .post<ContactResponse>(`${this.apiUrl}/send`, contactData, { headers })
      .pipe(
        catchError((error) => {
          let errorMessage = 'Failed to send message. Please try again.';

          if (error.status === 429) {
            errorMessage =
              'Too many contact form submissions. Please try again in 15 minutes.';
          } else if (error.status === 400 && error.error?.errors) {
            const validationErrors = error.error.errors
              .map((err: any) => err.message)
              .join(', ');
            errorMessage = `Please fix the following: ${validationErrors}`;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getContactInfo(): Observable<ContactInfo> {
    return this.http.get<ContactInfo>(`${this.apiUrl}/info`).pipe(
      catchError((error) => {
        console.error('Error fetching contact info:', error);
        return throwError(
          () => new Error('Failed to load contact information')
        );
      })
    );
  }

  getUserTickets(): Observable<UserTicket[]> {
    return this.http.get<UserTicketsResponse>(`${this.apiUrl}/tickets`).pipe(
      map((response) => response.data),
      catchError((error) => {
        console.error('Error fetching user tickets:', error);
        let errorMessage = 'Failed to load your tickets';

        if (error.status === 401) {
          errorMessage = 'Please log in to view your tickets';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  getTicketById(ticketId: string): Observable<TicketDetails> {
    return this.http
      .get<TicketDetailsResponse>(`${this.apiUrl}/tickets/${ticketId}`)
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          console.error('Error fetching ticket details:', error);
          let errorMessage = 'Failed to load ticket details';

          if (error.status === 404) {
            errorMessage = "Ticket not found or you don't have access to it";
          } else if (error.status === 401) {
            errorMessage = 'Please log in to view ticket details';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  addTicketReply(
    ticketId: string,
    replyData: TicketReplyRequest
  ): Observable<TicketResponse> {
    return this.http
      .post<TicketReplyResponse>(
        `${this.apiUrl}/tickets/${ticketId}/reply`,
        replyData
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          console.error('Error adding ticket reply:', error);
          let errorMessage = 'Failed to add reply';

          if (error.status === 404) {
            errorMessage = "Ticket not found or you don't have access to it";
          } else if (error.status === 401) {
            errorMessage = 'Please log in to reply to tickets';
          } else if (error.status === 400 && error.error?.errors) {
            const validationErrors = error.error.errors
              .map((err: any) => err.message)
              .join(', ');
            errorMessage = `Please fix the following: ${validationErrors}`;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  formatTicketDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
    } catch (error) {
      return 'Unknown date';
    }
  }

  formatStatus(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return status;
    }
  }

  formatPriority(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'Urgent';
      case 'high':
        return 'High';
      case 'normal':
        return 'Normal';
      case 'low':
        return 'Low';
      default:
        return priority;
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'status-pending';
      case 'in_progress':
        return 'status-in-progress';
      case 'resolved':
        return 'status-resolved';
      case 'closed':
        return 'status-closed';
      default:
        return 'status-default';
    }
  }

  getPriorityClass(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'priority-urgent';
      case 'high':
        return 'priority-high';
      case 'normal':
        return 'priority-normal';
      case 'low':
        return 'priority-low';
      default:
        return 'priority-normal';
    }
  }

  hasNewAdminResponse(ticket: UserTicket): boolean {
    if (!ticket.last_admin_response) return false;
    if (!ticket.last_user_response) return true;

    const adminResponseTime = new Date(ticket.last_admin_response).getTime();
    const userResponseTime = new Date(ticket.last_user_response).getTime();

    return adminResponseTime > userResponseTime;
  }
}
